// Card 16, shipping lanes (R2646, R3072; ui-map-interface.md §17.3.16,
// v2-the-deep.md §7.2; mockup round 028 card 16, round 088 card 09 and
// round 139 cards 20, 05 and 13).
//
// A shipping lane is a course plot that a commander of the viewer's
// faction marked with the LANE switch. The piece draws each lane of the
// faction in the faction ink, along its legs, with the leg back of each
// loop part. A lane starts at its first leg (R2729): a loop part opens at
// a leg and never at the fleet, so a lane that loops from its first leg
// draws the leg back to it, as the course of a fleet sails it.
//
// **THE LIT ROAD** (R3072). The owner picked round 139 card 20 Lit Road
// with the double rule of card 05. A lane is a road between two thin
// rules, and the road lights where a fleet of the faction takes a lift:
// inside the reach of a Fanal of the faction that acts, and on lit
// water (§7.1). `lanelit.ts` cuts each track of a lane into these runs.
// The modern reading draws the two rules and nothing between them on an
// unlit run (R3175); a lit run brightens the rules, fills the road with
// the faction ink (pale on lit water) and ticks across the road at each
// end, and one relay point runs along it, light to light (`relays`, drawn
// by the sea object layer, because it moves). A cap closes the road at the
// end of a lane. The historical reading is card 20 alone (R3175): one
// dashed pencil track on an unlit run, a ruled double line close round
// the track on a lit one with a bar at each end, a bar at the end of the
// lane, the level course of each long own leg (R3188) and "Lts in line" between two
// chained lights. A faction mate's lane is narrower and fainter in both.
//
// **THE HUNTED STRETCH** (card 13). A pirate contact in the picture near
// an own lane hunts the stretch nearest it: the modern reading draws a
// toothed edge in the NPC ink outside the road on the side of the pirate,
// and a dashed bearing to it; the historical one writes "Corsairs
// reported" there and rings the pirate with dots.
//
// **THE CHAIN AN ENEMY SEES.** A hostile faction receives no lane (§7.2
// rule 3), but every listener hears a Fanal (§11.3 rule 1). Two
// hostile Fanals of one faction at most `lane_chain_px` plus a small
// tolerance apart draw the straight segment between them, dashed in the
// ink of that faction: the reader learns that a lit road runs there, not
// its track.
//
// **THE QUIET LANE** (R3097, `v2-the-deep.md` §7.2 rule 13). A lane that
// the server marks quiet (`CoursePlot.quiet`) draws dimmer in both
// readings, so the faction map shows the roads in use. It keeps its
// rules, its cap and its shape. The modern reading dims each rule; a lit
// stretch stays lit but dimmer, because a light still lifts a fleet
// there. The historical reading takes a lighter pencil and, from 1x,
// writes "quiet lane" along the lane in the chart hand.
//
// **THE LANE GAP** (R3097, rule 14). Each gap point of a lane
// (`CoursePlot.gaps`, the chain point that one Fanal closes the gap
// with) draws the ghost light of the guide of the chain (R3066) at a low
// alpha, a short dashed bracket across the road and, from 1x, a tag:
// "GAP" in the modern reading, "Lt missing" in the historical one. A gap
// of a quiet lane draws dimmer. The ghost is one atlas cell
// (`ghostLightKey`), so the mark packs with the rest of the piece.
//
// **THE LANES ARE THE SERVER'S.** The own lanes are the plots of
// `Empire.plots` with `lane` set, and the lanes of the faction mates ride
// `Empire.lanes`. The server sends both only while the flag `routes` is
// latched (§14.1 rule 5), so the piece reads no flag: a world with the
// flag off sends no lane, and the chart of today stands.
//
// **COST.** A cheap batch: the piece packs on a State frame, so a light
// that starts or stops to act, a lane that changes and a pirate contact
// that comes, moves or goes each pack it again. The tracks and the lit
// water of a lane are measured again only when the lanes or the hello
// change. The relay point is the one thing a frame draws.
//
// **A LANE IS A MAP OBJECT** (round 088 card 09, R2658). The piece answers
// a pick on its track with the mark id of the lane (`laneMarkId` of
// `ui/cards/lane.ts`): a press opens the lane card, and a long press of a
// finger peeks it. The pick reaches half the road past the tolerance of a
// mark, so a press on the road takes the lane.
//
// **VIEW STANDS A LANE OUT** (R2709). VIEW of a lane row of FLEET · ROUTES
// names the lane (`view`): the next frame packs again, and for `view_sec`
// seconds that lane draws a band of `view_band_alpha` and a solid track
// `view_width` px wide over its road. The end of the time is an edge of
// its own, so the piece packs again once and stays static.

import { NpcKind, SeaObjectKind, type CoursePlot } from '../../../gen/proto/aquaphobia';
import { CHART, MARKER_NEUTRAL, NPC_INK } from '../../../gen/presentation';
import { chart as S } from '../../../gen/strings';
import { loopParts } from '../../../ui/plotline';
import { factionColor, mpxToPx } from '../../../world';
import { laneMarkId } from '../../../ui/cards/lanemark';
import { laneTracks, type ChainPt } from '../../../lanechain';
import {
  foeChains, lightIntervals, lightStops, litWaterCells, measureLine, measureTrack, nearest, offsetLine, pointAt,
  runsOf, subLine, waterIntervals, type FoeLight, type Interval, type LaneLine, type RunKind, type XY,
} from '../../../lanelit';
import { F_JOIN0, F_JOIN1, F_ROUND } from '../../layer';
import type { ChartContext, ChartSinks, PickHit, Piece } from '../context';
import type { SegmentSink } from '../../layers/batches';
import { CHART_INK, faceOf } from './shared';
import { stepPx } from '../steps';
import { runWidth } from '../../../render/text-run';
import { ghostLightKey } from '../../../render/lane-light-looks';

/** One leg of a lane, in world px. */
export interface LaneLeg { x0: number; y0: number; x1: number; y1: number }

/** The legs of one lane: the run between its points, and the leg back of
 *  each loop part to the point it opens at (R2729). */
export function laneLegs(p: CoursePlot): LaneLeg[] {
  const pts = p.legs.map((w) => [mpxToPx(w.xMpx), mpxToPx(w.yMpx)] as const);
  const out: LaneLeg[] = [];
  for (let i = 1; i < pts.length; i++) {
    out.push({ x0: pts[i - 1][0], y0: pts[i - 1][1], x1: pts[i][0], y1: pts[i][1] });
  }
  for (const part of loopParts(p.legs)) {
    const a = pts[part.last], b = pts[part.open];
    out.push({ x0: a[0], y0: a[1], x1: b[0], y1: b[1] });
  }
  return out;
}

/** A course in whole degrees from north, three figures. */
function courseDeg(dx: number, dy: number): string {
  const deg = (Math.round((Math.atan2(dx, -dy) * 180) / Math.PI) + 360) % 360;
  return String(deg).padStart(3, '0');
}

/** The course of a leg in whole degrees from north, three figures, as
 *  card 07 writes it: `045°`. */
export function legCourse(l: LaneLeg): string {
  return S.lane_course({ deg: courseDeg(l.x1 - l.x0, l.y1 - l.y0) });
}

/** A key of the lanes of a frame: the ids, the owners, the points, the
 *  quiet mark and the gap points (R3097). */
function laneKey(lanes: readonly CoursePlot[]): string {
  return lanes.map((p) => `${p.owner}:${p.id}:${p.legs.map((w) => `${w.xMpx},${w.yMpx},${w.loop ? 1 : 0}${w.passes}`).join(';')}`
    + `:${p.quiet ? 1 : 0}:${p.gaps.map((g) => `${g.xMpx},${g.yMpx}`).join(';')}`).join('|');
}

/** One run of a drawn track: its stretch, what lights it, its points and
 *  its two rules: half the road each side in the modern reading, the
 *  ruled double line of a lit run in the historical one. */
export interface DrawnRun { a: number; b: number; kind: RunKind; pts: XY[]; rules: [XY[], XY[]] }

/** One track of a drawn lane, cut into runs. `end` is the lane end, where
 *  the cap closes the road, or null for a track with no end. */
export interface DrawnTrack { line: LaneLine; runs: DrawnRun[]; end: { x: number; y: number; ux: number; uy: number } | null }

/** The stretch of an own lane that a pirate contact hunts (card 13). */
export interface Hunt {
  /** The hunted stretch, its toothed edge and the side of the pirate. */
  pts: XY[];
  teeth: XY[];
  side: number;
  /** The point of the lane nearest the pirate, and the pirate. */
  qx: number; qy: number;
  px: number; py: number;
}

/** A word along a lane: the line it lies on, left to right. */
interface LaneWord { pts: { x: number; y: number }[]; text: string }

/** One lane gap (R3097): the chain point, and the direction of the track
 *  there, so the bracket stands across the road. */
export interface DrawnGap { x: number; y: number; ux: number; uy: number }

/** The gap of a chain point: the direction of the nearest track there. */
function gapOf(tracks: readonly LaneLine[], x: number, y: number): DrawnGap {
  let best: { ux: number; uy: number } | null = null, bestD = Infinity;
  for (const l of tracks) {
    if (!(l.len > 0)) continue;
    const h = nearest(l, x, y);
    if (h[1] < bestD) { bestD = h[1]; best = pointAt(l, h[0]); }
  }
  return { x, y, ux: best?.ux ?? 1, uy: best?.uy ?? 0 };
}

/** One drawn lane. */
interface DrawnLane {
  id: string;
  own: boolean;
  legs: LaneLeg[];
  tracks: DrawnTrack[];
  hunts: Hunt[];
  /** "Lts in line" between two chained lights (the historical reading). */
  lines: LaneWord[];
  corsairs: LaneWord[];
  /** A quiet lane (R3097), and its note in the historical reading. */
  quiet: boolean;
  note: LaneWord | null;
  /** The lane gaps (R3097). */
  gaps: DrawnGap[];
}

/** The relay point of one stretch a light lights (card 20): the sea object
 *  layer draws it once a frame, `mul` the alpha of a faction mate's lane. */
export interface RelayRun { line: LaneLine; phase: number; mul: number }

/** The segment between two hostile Fanals that chain. */
export interface FoeSegment { x0: number; y0: number; x1: number; y1: number; col: number }

/** No relay point: the stretches of a historical pack, or a hidden card. */
export const NO_RELAYS: readonly RelayRun[] = [];

/** The points of a word along a line, turned so it reads left to right. */
function wordLine(pts: readonly XY[]): { x: number; y: number }[] {
  const out = pts.map((p) => ({ x: p[0], y: p[1] }));
  const a = out[0], b = out[out.length - 1];
  return a && b && b.x < a.x ? out.reverse() : out;
}

/** Strokes a line of points, one segment a leg. */
function stroke(seg: SegmentSink, pts: readonly XY[], width: number, col: number, alpha: number,
  flags = 0, on = 0, off = 0): void {
  if (!(alpha > 0)) return;
  for (let i = 1; i < pts.length; i++) {
    seg.line(pts[i - 1][0], pts[i - 1][1], pts[i][0], pts[i][1], width, col, alpha, flags, on, off);
  }
}

/** `tan` of half the turn from the direction `a` to the direction `b`,
 *  the slope of the bisector cut of `F_JOIN0` (R3191). A turn back on
 *  itself takes a steep finite slope. */
function halfTurn(ax: number, ay: number, bx: number, by: number): number {
  const sin = ax * by - ay * bx, cos = ax * bx + ay * by;
  const k = sin / Math.max(1 + cos, 1e-4);
  return Math.max(-1e4, Math.min(1e4, k));
}

/** The road fill of a polyline: one segment a leg, and each two legs that
 *  meet join on the bisector of their corner (R3191). A rectangle a leg
 *  drew the corner twice on its inner side and cut it on its outer side. */
export function fillLegs(seg: SegmentSink, legs: readonly LaneLeg[], width: number, col: number, alpha: number): void {
  if (!(alpha > 0)) return;
  const live = legs.filter((l) => Math.hypot(l.x1 - l.x0, l.y1 - l.y0) > 0);
  const dir = live.map((l) => {
    const L = Math.hypot(l.x1 - l.x0, l.y1 - l.y0);
    return [(l.x1 - l.x0) / L, (l.y1 - l.y0) / L] as const;
  });
  const joins = (i: number): boolean => i + 1 < live.length
    && live[i].x1 === live[i + 1].x0 && live[i].y1 === live[i + 1].y0;
  for (let i = 0; i < live.length; i++) {
    const l = live[i];
    let flags = 0, k0 = 0, k1 = 0;
    if (i > 0 && joins(i - 1)) { flags |= F_JOIN0; k0 = halfTurn(...dir[i - 1], ...dir[i]); }
    if (joins(i)) { flags |= F_JOIN1; k1 = -halfTurn(...dir[i], ...dir[i + 1]); }
    seg.line(l.x0, l.y0, l.x1, l.y1, width, col, alpha, flags, k0, k1);
  }
}

/** The legs of a polyline. */
function legsOf(pts: readonly XY[]): LaneLeg[] {
  const out: LaneLeg[] = [];
  for (let i = 1; i < pts.length; i++) out.push({ x0: pts[i - 1][0], y0: pts[i - 1][1], x1: pts[i][0], y1: pts[i][1] });
  return out;
}

/** A short line across a track at the distance `s`, `half` px each side. */
function across(seg: SegmentSink, l: LaneLine, s: number, half: number, width: number, col: number, alpha: number): void {
  const p = pointAt(l, s), nx = -p.uy, ny = p.ux;
  seg.line(p.x - nx * half, p.y - ny * half, p.x + nx * half, p.y + ny * half, width, col, alpha);
}

/** The toothed edge of a hunted stretch, `side` the side of the pirate. */
function teethOf(l: LaneLine, side: number, f: typeof CHART.pieces.shipping_lanes.fig): XY[] {
  const out: XY[] = [];
  const step = Math.max(0.5, f.teeth_step_px);
  let k = 0;
  for (let s = 0; s <= l.len; s += step, k++) {
    const p = pointAt(l, s);
    const taper = f.teeth_taper_px > 0 ? Math.min(1, s / f.teeth_taper_px, (l.len - s) / f.teeth_taper_px) : 1;
    const o = side * (f.teeth_off_px + (k % 2 ? f.teeth_amp_px * taper : 0));
    out.push([p.x - p.uy * o, p.y + p.ux * o]);
  }
  return out;
}

export class ShippingLanesPiece implements Piece {
  readonly id = 'shipping_lanes' as const;
  readonly tier = 'sea' as const;
  readonly pack = 'static' as const;
  /** The lanes of the season running now: a replay film hides them. */
  readonly film = 'hide' as const;
  readonly rebuildOn = ['hello', 'state', 'reading', 'zoomband'] as const;

  /** The legs of the last pack. The tests read them. */
  legs: LaneLeg[] = [];
  /** The legs of the own lanes and of the faction mates' lanes of the
   *  last pack: the two draw apart. */
  ownLegs: LaneLeg[] = [];
  mateLegs: LaneLeg[] = [];
  /** The lanes of the last pack, mates first, so an own lane draws over. */
  private drawn: DrawnLane[] = [];
  /** The relay points of the last pack (the modern reading only). */
  relays: readonly RelayRun[] = NO_RELAYS;
  /** The chain segments of the hostile Fanals in the picture. */
  foeSegments: FoeSegment[] = [];
  /** The half road, world px: the pick reaches it past the tolerance. */
  private halfBand = 0;
  /** The faction of the last pack: every lane it draws is of it. */
  private faction = 0;
  /** The empire frame and the key of the lanes of the last pack. */
  private frame: unknown = null;
  private key = '';
  /** The measured tracks and their lit water, kept while the lanes and the
   *  hello stay. */
  private geoKey = '';
  private geoHello: unknown = null;
  private geo: { tracks: LaneLine[]; water: Interval[][] }[] = [];
  /** The sea object revision, the viewer and the chain of the last walk of
   *  the hostile lights. */
  private foeRev = -1;
  private foeFaction = -1;
  private foeWithin = -1;
  /** VIEW (R2709): the mark id a press asked for, the one drawn, until
   *  when it draws (the frame clock), and its legs of the last pack. */
  private viewWant = '';
  private viewId = '';
  private viewUntil = 0;
  viewLegs: LaneLeg[] = [];

  /** VIEW of FLEET · ROUTES: the lane of this mark id stands out from the
   *  next frame on, for `view_sec` seconds. */
  view(markId: string): void {
    this.viewWant = markId;
  }

  /** Every lane of the faction: the own plots with LANE, then the mates'. */
  private lanes(ctx: ChartContext): CoursePlot[] {
    return [...ctx.state.plots.filter((p) => p.lane), ...ctx.state.factionLanes];
  }

  visible(ctx: ChartContext): boolean {
    this.walkFoes(ctx);
    return ctx.state.factionLanes.length > 0 || ctx.state.plots.some((p) => p.lane) || this.foeSegments.length > 0;
  }

  /** The reach of a Fanal of the catalog, world px; 0 for none. */
  private reach(ctx: ChartContext): number {
    return ctx.state.seaObject(SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT)?.reachPx ?? 0;
  }

  /**
   * Walks the hostile Fanals again when the picture changed (one
   * compare a frame): each two of one faction that chain draw a segment.
   */
  private walkFoes(ctx: ChartContext): void {
    const store = ctx.state.seaObjects, viewer = ctx.state.faction;
    const chain = ctx.state.rules.seaObjects?.laneChainPx ?? 0;
    const within = chain > 0 ? chain + ctx.cfg.pieces.shipping_lanes.fig.foe_chain_tol_px : 0;
    if (store.rev === this.foeRev && viewer === this.foeFaction && within === this.foeWithin) return;
    this.foeRev = store.rev; this.foeFaction = viewer; this.foeWithin = within;
    if (!(within > 0)) { this.foeSegments = []; return; }
    const foes: FoeLight[] = store.list.filter((r) => !r.heard && r.kind === SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT
      && r.faction !== viewer && r.faction !== 0);
    this.foeSegments = foeChains(foes, within).map(([a, b]) => ({ x0: a.x, y0: a.y, x1: b.x, y1: b.y, col: factionColor(a.faction) }));
  }

  rebuild(ctx: ChartContext): void {
    const lanes = this.lanes(ctx);
    const fig = ctx.cfg.pieces.shipping_lanes.fig;
    this.frame = ctx.state.empire;
    this.key = laneKey(lanes);
    this.faction = ctx.state.faction;
    this.walkFoes(ctx);
    // The tracks and the lit water move only with the lanes and the map.
    if (this.geoKey !== this.key || this.geoHello !== ctx.state.hello) {
      this.geoKey = this.key;
      this.geoHello = ctx.state.hello;
      const cells = litWaterCells(ctx.state.fairways, ctx.state.buoys);
      this.geo = lanes.map((p) => {
        const tracks = laneTracks(p).map(measureTrack);
        return { tracks, water: tracks.map((l) => waterIntervals(l, cells, fig.water_step_px)) };
      });
    }
    const reach = this.reach(ctx);
    // The lights of the faction that act: an own light and a mate's light
    // both lift the fleets of the faction on its lanes (§11.3).
    const lights: ChainPt[] = ctx.state.seaObjects.list.filter((r) => !r.heard && r.armed
      && r.kind === SeaObjectKind.SEA_OBJECT_KIND_LANE_LIGHT && r.faction === this.faction);
    const pirates = (ctx.scene.fleets?.chartFleets() ?? []).filter((f) => f.npc === NpcKind.NPC_KIND_PIRATE);
    const historical = ctx.reading === 'historical';
    const drawn: DrawnLane[] = lanes.map((p, i) => {
      const own = p.owner === 0;
      const g = this.geo[i];
      const hw = historical ? ctx.cfg.pieces.shipping_lanes.historical.fig.rule_gap_px : (own ? fig.band : fig.mate_band) / 2;
      const tracks: DrawnTrack[] = g.tracks.map((l, ti) => {
        const runs = runsOf(l.len, lightIntervals(l, lights, reach), g.water[ti])
          .map((r) => {
            const pts = subLine(l, r.a, r.b);
            return { ...r, pts, rules: [offsetLine(pts, -hw), offsetLine(pts, hw)] as [XY[], XY[]] };
          });
        // The first track is the lane itself: an open one ends at its last point.
        const end = ti === 0 && !l.closed && l.len > 0 ? pointAt(l, l.len) : null;
        return { line: l, runs, end };
      });
      const gaps = p.gaps.map((gp) => gapOf(g.tracks, mpxToPx(gp.xMpx), mpxToPx(gp.yMpx)));
      const note = p.quiet && historical ? this.quietNote(g.tracks[0], ctx) : null;
      const lane: DrawnLane = {
        id: laneMarkId(p.owner, p.id), own, legs: laneLegs(p), tracks, hunts: [], lines: [], corsairs: [],
        quiet: p.quiet, note, gaps,
      };
      if (own) this.hunt(lane, pirates, ctx);
      if (own && historical) this.chain(lane, lights, reach, ctx);
      return lane;
    });
    // A mate's lane draws first, so an own lane stands over it.
    this.drawn = [...drawn.filter((d) => !d.own), ...drawn.filter((d) => d.own)];
    this.legs = drawn.flatMap((d) => d.legs);
    this.ownLegs = drawn.filter((d) => d.own).flatMap((d) => d.legs);
    this.mateLegs = drawn.filter((d) => !d.own).flatMap((d) => d.legs);
    this.viewLegs = this.viewId ? drawn.find((d) => d.id === this.viewId)?.legs ?? [] : [];
    this.halfBand = fig.band / 2;
    const mf = ctx.cfg.pieces.shipping_lanes.modern.fig;
    this.relays = historical ? NO_RELAYS : this.drawn.flatMap((d) => d.tracks.flatMap((t) => t.runs
      .filter((r) => r.kind === 'light')
      .map((r) => ({
        line: measureLine(r.pts), phase: mf.relay_phase * r.a,
        mul: (d.own ? 1 : fig.mate_mul) * (d.quiet ? mf.quiet_lane_lit_mul : 1),
      }))));
  }

  /** The note along a quiet lane in the historical reading: "quiet lane"
   *  about the middle of its first track, off the side of "Lts in line". */
  private quietNote(l: LaneLine | undefined, ctx: ChartContext): LaneWord | null {
    if (!l || !(l.len > 0)) return null;
    const hf = ctx.cfg.pieces.shipping_lanes.historical.fig;
    const mid = l.len * hf.quiet_note_at;
    const pts = subLine(l, Math.max(0, mid - hf.quiet_note_half_px), Math.min(l.len, mid + hf.quiet_note_half_px));
    return { pts: wordLine(offsetLine(pts, hf.quiet_note_dy)), text: S.lane_quiet_historical };
  }

  /** The stretches of an own lane that pirate contacts hunt: the nearest
   *  contact first, and no two on one track that overlap. */
  private hunt(lane: DrawnLane, pirates: readonly { x: number; y: number }[], ctx: ChartContext): void {
    const f = ctx.cfg.pieces.shipping_lanes.fig;
    const hf = ctx.cfg.pieces.shipping_lanes.historical.fig;
    for (const t of lane.tracks) {
      const l = t.line;
      const near = pirates.map((p) => ({ p, h: nearest(l, p.x, p.y) }))
        .filter((o) => o.h[1] <= f.hunt_px).sort((a, b) => a.h[1] - b.h[1]);
      const kept: Interval[] = [];
      for (const { p, h } of near) {
        const a = Math.max(0, h[0] - f.hunt_half_px), b = Math.min(l.len, h[0] + f.hunt_half_px);
        if (!(b > a) || kept.some(([x, y]) => a < y && b > x)) continue;
        kept.push([a, b]);
        const q = pointAt(l, h[0]);
        const side = Math.sign((p.x - q.x) * -q.uy + (p.y - q.y) * q.ux) || 1;
        const pts = subLine(l, a, b);
        lane.hunts.push({ pts, teeth: teethOf(measureLine(pts), side, f), side, qx: q.x, qy: q.y, px: p.x, py: p.y });
        lane.corsairs.push({ pts: wordLine(offsetLine(pts, side * hf.corsair_dy)), text: S.lane_corsairs });
      }
    }
  }

  /** "Lts in line" between each two lights of an own lane whose reaches
   *  meet, with the course from the one to the other: both stand in one
   *  light run of the track (R3190). */
  private chain(lane: DrawnLane, lights: readonly ChainPt[], reach: number, ctx: ChartContext): void {
    const hf = ctx.cfg.pieces.shipping_lanes.historical.fig;
    for (const t of lane.tracks) {
      const stops = lightStops(t.line, lights, reach);
      const lit = t.runs.filter((r) => r.kind === 'light');
      for (let i = 1; i < stops.length; i++) {
        const A = stops[i - 1], B = stops[i];
        if (B.s <= A.s || !lit.some((r) => A.s >= r.a && B.s <= r.b)) continue;
        const deg = courseDeg(B.light.x - A.light.x, B.light.y - A.light.y);
        lane.lines.push({ pts: wordLine(offsetLine(subLine(t.line, A.s, B.s), hf.line_dy)), text: S.lane_lights_line({ deg }) });
      }
    }
  }

  /** The lane whose track passes nearest a world point, or null. The hit
   *  stands at the nearest point of the track. */
  pick(wx: number, wy: number, tolPx: number): PickHit | null {
    const tol = tolPx + this.halfBand;
    let best: PickHit | null = null;
    let bestD = Infinity;
    for (const p of this.drawn) {
      for (const l of p.legs) {
        const dx = l.x1 - l.x0, dy = l.y1 - l.y0;
        const len2 = dx * dx + dy * dy;
        const t = len2 > 0 ? Math.max(0, Math.min(1, ((wx - l.x0) * dx + (wy - l.y0) * dy) / len2)) : 0;
        const x = l.x0 + t * dx, y = l.y0 + t * dy;
        const d = Math.hypot(wx - x, wy - y);
        if (d <= tol && d < bestD) { best = { kind: 'mark', id: p.id, x, y }; bestD = d; }
      }
    }
    return best;
  }

  /** A new Empire frame with other lanes is an edge no reason names: one
   *  compare of the frame a frame, and a key only when the frame moved. */
  outgrown(ctx: ChartContext): boolean {
    // VIEW (R2709): a press and the end of its time are each one edge.
    if (this.viewWant) {
      this.viewId = this.viewWant;
      this.viewWant = '';
      this.viewUntil = ctx.timeS + ctx.cfg.pieces.shipping_lanes.fig.view_sec;
      return true;
    }
    if (this.viewId && ctx.timeS >= this.viewUntil) {
      this.viewId = '';
      return true;
    }
    if (ctx.state.empire === this.frame) return false;
    this.frame = ctx.state.empire;
    return laneKey(this.lanes(ctx)) !== this.key;
  }

  emit(sinks: ChartSinks, ctx: ChartContext): void {
    const t = ctx.cfg.pieces.shipping_lanes;
    const ink = factionColor(this.faction);
    const historical = ctx.reading === 'historical';
    for (const d of this.drawn) {
      if (historical) this.emitHistorical(sinks, ctx, d, ink);
      else this.emitModern(sinks, ctx, d, ink);
      this.emitGaps(sinks, ctx, d, ink, historical);
    }
    for (const s of this.foeSegments) {
      sinks.segments.line(s.x0, s.y0, s.x1, s.y1, t.fig.foe_chain_width, s.col, t.fig.foe_chain_alpha, F_ROUND,
        t.fig.foe_chain_dash_on, t.fig.foe_chain_dash_off);
    }
    const alpha = historical ? t.historical.alpha : t.modern.alpha;
    fillLegs(sinks.segments, this.viewLegs, t.fig.band, ink, t.fig.view_band_alpha);
    for (const l of this.viewLegs) {
      sinks.segments.line(l.x0, l.y0, l.x1, l.y1, t.fig.view_width, ink, alpha);
    }
  }

  /** Card 20 with the rules of card 05: the two rules of an unlit run,
   *  the filled road and the ticks of a lit one, the cap, and the toothed
   *  edge of a hunted stretch. No line runs between the rules (R3175). */
  private emitModern(sinks: ChartSinks, ctx: ChartContext, d: DrawnLane, ink: number): void {
    const t = ctx.cfg.pieces.shipping_lanes, r = t.modern, f = r.fig, seg = sinks.segments;
    const m = d.own ? 1 : t.fig.mate_mul, hw = (d.own ? t.fig.band : t.fig.mate_band) / 2;
    // A quiet lane (R3097): an unlit stretch and the cap take `qd`, a lit
    // stretch `ql`.
    const qd = d.quiet ? m * f.quiet_lane_mul : m, ql = d.quiet ? m * f.quiet_lane_lit_mul : m;
    const pale = CHART_INK.pale;
    for (const tr of d.tracks) {
      for (const run of tr.runs) {
        if (!run.kind) continue;
        const water = run.kind === 'water';
        fillLegs(seg, legsOf(run.pts), 2 * hw, water ? pale : ink, (water ? f.water_fill_alpha : f.fill_alpha) * ql);
      }
      for (const run of tr.runs) {
        const lit = run.kind !== null;
        for (const rule of run.rules) {
          stroke(seg, rule, lit ? r.width : f.unlit_width, ink, lit ? r.alpha * ql : f.unlit_alpha * qd, F_ROUND);
        }
        if (!lit) continue;
        const half = d.own ? f.tick_half_px : f.mate_tick_half_px;
        for (const s of [run.a, run.b]) across(seg, tr.line, s, half, f.tick_width, run.kind === 'water' ? pale : ink, f.tick_alpha * ql);
      }
      this.cap(seg, tr, hw, t.fig.end_width, ink, t.fig.end_alpha * qd);
    }
    for (const h of d.hunts) {
      stroke(seg, h.teeth, t.fig.teeth_width, NPC_INK.color, t.fig.teeth_alpha, F_ROUND);
      seg.line(h.px, h.py, h.qx, h.qy, t.fig.hunt_line_width, NPC_INK.color, t.fig.hunt_line_alpha, F_ROUND,
        t.fig.hunt_dash_on, t.fig.hunt_dash_off);
    }
  }

  /** Card 20 engraved (R3175): one dashed pencil track where unlit, a
   *  ruled double line where lit with a bar at each end, a bar at the lane
   *  end, the courses, "Lts in line" and "Corsairs reported". */
  private emitHistorical(sinks: ChartSinks, ctx: ChartContext, d: DrawnLane, ink: number): void {
    const t = ctx.cfg.pieces.shipping_lanes, r = t.historical, f = r.fig, seg = sinks.segments;
    const m = d.own ? 1 : f.mate_mul;
    const on = r.dash[0] ?? 0, off = r.dash[1] ?? 0;
    // A quiet lane (R3097) takes a lighter pencil: a thinner track, `qd`
    // on an unlit stretch and the lane end, `ql` on a lit one.
    const qd = d.quiet ? m * f.quiet_lane_mul : m, ql = d.quiet ? m * f.quiet_lane_lit_mul : m;
    const width = d.quiet ? f.quiet_lane_width : d.own ? r.width : f.mate_width;
    for (const tr of d.tracks) {
      for (const run of tr.runs) {
        if (!run.kind) { stroke(seg, run.pts, width, ink, r.alpha * qd, 0, on, off); continue; }
        // The one rule takes the pale ink on lit water: the water lifts there.
        stroke(seg, run.rules[0], f.rule_width, ink, f.rule_alpha * ql);
        stroke(seg, run.rules[1], f.rule_width, run.kind === 'water' ? CHART_INK.pale : ink, f.rule_alpha * ql);
        const half = d.own ? f.bar_half_px : f.mate_bar_half_px;
        for (const s of [run.a, run.b]) across(seg, tr.line, s, half, f.bar_width, ink, f.bar_alpha * ql);
      }
      this.cap(seg, tr, f.end_half_px, f.end_width, ink, t.fig.end_alpha * qd);
    }
    if (d.note && ctx.zoom >= f.quiet_note_zoom) {
      ctx.glyphs.wordAlong(sinks.quads, d.note.text, d.note.pts, faceOf('historical'), t.text.quiet, ink,
        f.quiet_note_alpha * m, { anchor: 'centre' });
    }
    if (!d.own) return;
    for (const l of d.legs) this.course(sinks, ctx, l, ink);
    const face = faceOf('historical');
    if (ctx.zoom >= f.char_zoom) {
      for (const w of d.lines) ctx.glyphs.wordAlong(sinks.quads, w.text, w.pts, face, t.text.line, ink, f.line_alpha, { anchor: 'centre' });
    }
    for (const w of d.corsairs) {
      ctx.glyphs.wordAlong(sinks.quads, w.text, w.pts, face, t.text.corsairs, NPC_INK.color, f.corsair_alpha, { anchor: 'centre' });
    }
    for (const h of d.hunts) {
      sinks.circles.dashed(h.px, h.py, f.corsair_ring_px, f.corsair_ring_width, Math.round(f.corsair_ring_dots),
        f.corsair_ring_fill, NPC_INK.color, f.corsair_ring_alpha);
    }
  }

  /** The lane gaps of a lane (R3097): the ghost light at each gap point, a
   *  dashed bracket across the road and, from `gap_tag_zoom`, the tag. A
   *  faction mate's lane takes the mate figure of the reading, and a quiet
   *  lane its quiet figure. */
  private emitGaps(sinks: ChartSinks, ctx: ChartContext, d: DrawnLane, ink: number, historical: boolean): void {
    if (d.gaps.length === 0) return;
    const t = ctx.cfg.pieces.shipping_lanes, f = t.fig, seg = sinks.segments;
    const rf = historical ? t.historical.fig : t.modern.fig;
    const m = (d.own ? 1 : historical ? t.historical.fig.mate_mul : f.mate_mul) * (d.quiet ? rf.quiet_lane_mul : 1);
    const ghost = ctx.glyphs.mark(ghostLightKey(historical, ink));
    const tag = ctx.zoom >= f.gap_tag_zoom ? (historical ? S.lane_gap_historical : S.lane_gap_tag) : '';
    const face = faceOf(historical ? 'historical' : 'modern');
    const h = f.gap_bracket_half_px, arm = f.gap_bracket_arm_px, a = f.gap_bracket_alpha * m;
    for (const g of d.gaps) {
      sinks.quads.sprite(g.x, g.y, 0, ghost, MARKER_NEUTRAL, f.gap_ghost_alpha * m, 1, 0, false);
      const nx = -g.uy, ny = g.ux;
      seg.line(g.x - nx * h, g.y - ny * h, g.x + nx * h, g.y + ny * h, f.gap_bracket_width, ink, a, F_ROUND,
        f.gap_dash_on, f.gap_dash_off);
      for (const k of [-h, h]) {
        const ex = g.x + nx * k, ey = g.y + ny * k;
        seg.line(ex - g.ux * arm, ey - g.uy * arm, ex + g.ux * arm, ey + g.uy * arm, f.gap_bracket_width, ink, a, F_ROUND);
      }
      if (tag) {
        ctx.glyphs.word(sinks.quads, tag, g.x - nx * f.gap_tag_off_px, g.y - ny * f.gap_tag_off_px, face, t.text.gap, ink,
          f.gap_tag_alpha * m, { anchor: 'centre' });
      }
    }
  }

  /** The cap across the lane at its end, `hw` px each side of the track:
   *  across the road of card 05, or the bar of card 20. */
  private cap(seg: SegmentSink, tr: DrawnTrack, hw: number, width: number, ink: number, alpha: number): void {
    if (!tr.end) return;
    const e = tr.end;
    seg.line(e.x + e.uy * hw, e.y - e.ux * hw, e.x - e.uy * hw, e.y + e.ux * hw, width, ink, alpha);
  }

  /** Card 07: the course of one own leg of at least `course_min_px`,
   *  level on the chart, `course_gap_px` clear of the leg on its left. */
  private course(sinks: ChartSinks, ctx: ChartContext, l: LaneLeg, ink: number): void {
    const f = ctx.cfg.pieces.shipping_lanes.historical.fig;
    const dx = l.x1 - l.x0, dy = l.y1 - l.y0, len = Math.hypot(dx, dy);
    if (len < f.course_min_px) return;
    // The left normal of the leg.
    const nx = dy / len, ny = -dx / len;
    const face = faceOf('historical'), step = ctx.cfg.pieces.shipping_lanes.text.course;
    const text = legCourse(l);
    // A level word stands clear of the leg when its centre stands off the
    // track by the gap plus the reach of its box along the normal. The
    // pivot is on the track, so the band scale grows the gap with the word.
    const hw = runWidth(text, ctx.glyphs.stepMetrics(face, step)) / 2, hh = stepPx(face, step) / 2;
    const off = f.course_gap_px + hw * Math.abs(nx) + hh * Math.abs(ny);
    const px = l.x0 + dx * f.course_at, py = l.y0 + dy * f.course_at;
    ctx.glyphs.word(sinks.quads, text, px + nx * off, py + ny * off, face, step, ink, f.course_alpha,
      { anchor: 'centre', pivot: { x: px, y: py } });
  }
}

/** The one instance the registry holds. */
export const shippingLanes = new ShippingLanesPiece();
